import React, { useContext, useEffect, useState } from 'react'
import { Button, Dialog, Notification, toast } from 'components/ui'
import { CompanyContext } from 'contexts/CompanyContext'
import { BsCheckCircleFill, BsCircle } from 'react-icons/bs';
import { api as Api } from 'api/http';
import { SessionContext } from 'contexts/SessionContext';
import { CalendarActiveSocialNetworkContext } from 'contexts/CalendarActiveSocialNetworkContext';
import { Loading } from 'components/shared';
import { Trans } from 'react-i18next';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

const FacebookPageSelectModal = () => {

    const { company, updateSocials, companySocials } = useContext(CompanyContext);
    const { setFacebookPageSelectModalOpened } = useContext(CalendarActiveSocialNetworkContext);
    const { updateUserData } = useContext(SessionContext);
    const [ pages, setPages ] = useState([]);
    const [ pageID, setPageID ] = useState();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const { whitelabel } = useWhitelabel();

    const facebookSocialNetwork = companySocials?.filter(e => e.type == 'facebook')?.[0];

    const handleOnRequestClose = () => {
        if (!facebookSocialNetwork?.id) {
            setFacebookPageSelectModalOpened(false);
            return;
        }
        setIsLoading(true);
        Api.delete(`/socialNetworks/${facebookSocialNetwork.id}`).then(() => {
            updateUserData();
            updateSocials();
            setFacebookPageSelectModalOpened(false);
        });
    }

    const handleSubmit = () => {
        setIsSubmitting(true);
        Api.put(`/socialNetworks/${facebookSocialNetwork?.id}`, {}, { page_id: pageID }).then(() => {
            setIsLoading(true);
            updateUserData();
            updateSocials();
            setFacebookPageSelectModalOpened(false);
            toast.push(<Notification title="Facebook integrated successfully" type="success" />);
        }).catch((err) => {
            setIsLoading(false);
            setIsSubmitting(false);
            toast.push(<Notification title={err.message} type="danger" />);
        });
    }

    useEffect(() => {
        if (facebookSocialNetwork && !pages?.length) {
            setIsLoading(true);
            Api.get(`/socialNetworks/${facebookSocialNetwork?.id}/pages?fields=id,name,picture&limit=100`).then(({ data: pages }) => {
                setPages(pages);
                setIsLoading(false);
            }).catch((err) => {
                handleOnRequestClose();
                toast.push(<Notification title="An error ocurred. Please, try again later." type="danger" />);
            });
        }
    }, [companySocials]);

    return (
        <Dialog
            isOpen={true}
            closable={true}
            onRequestClose={handleOnRequestClose}
            overlayClassName="flex w-full h-full items-center justify-center"
            width={650}
        >
            {isLoading
                ? <>
                    <div className='flex flex-col items-center justify-center min-h-[300px]'>
                        <Loading type={'element'} /> 
                    </div>
                </> : <>
                    <img src="/img/others/facebook-banner-3.jpg" className='object-fit object-cover w-[120%] max-w-none h-[200px] -m-6 !mb-8' />
                    <div className='flex flex-col gap-8'>
                        <div className='flex flex-col gap-5'>
                            <h3 className='text-tertiary-900 mega-title'><Trans i18nKey={`facebookIntegration.title`}>Facebook Integration</Trans></h3>
                            <p className='text-tertiary-700 font-normal !text-sm'>
                                <Trans i18nKey={`facebookIntegration.dueToLimitations`}>Due to Facebook limitations, you can not connect your Facebook personal profile, you can only connect Facebook Pages.</Trans>
                                <Trans i18nKey={`facebookIntegration.selectFacebookPage`}>Select the Facebook Page you'd like to connect to {whitelabel?.name}.</Trans>
                            </p>
                        </div>

                        <div className='flex flex-col gap-2'>
                            <h6><Trans i18nKey={`facebookIntegration.facebookPages`}>Facebook Pages</Trans> ({pages?.length})</h6>
                            <div className="max-h-[210px] overflow-y-auto flex flex-col gap-2">
                                {pages && pages.map((item, key) => (
                                    <div onClick={() => setPageID(item?.id)} key={key} className={`cursor-pointer flex justify-between items-center gap-4 rounded-2xl border border-gray-200 dark:border-gray-600 border-solid py-2.5 px-4 transition-all hover:bg-primary-600/5 ${pageID == item?.id ? `!bg-primary-600/20 border-2 !border-primary-600 dark:!border-primary-600` : ``}`}>
                                        <div className='flex items-center gap-4'>
                                            <div>
                                                <div className='w-10 h-10 bg-primary-600/30 border-4 border-primary-600/10 flex items-center justify-center rounded-full overflow-hidden'>
                                                    <img src={item?.picture?.data?.url} className="w-full h-full" />
                                                </div>
                                            </div>
                                            <div className='flex flex-col gap-0 text-tertiary-800 dark:text-gray-100'>
                                                <div className='font-bold !text-sm'>{item?.name}</div>
                                            </div>
                                        </div>
                                        <div className='text-xl'>
                                            {pageID == item?.id
                                                ? <BsCheckCircleFill className='text-primary-600' />
                                                : <BsCircle className='text-tertiary-200 dark:text-gray-600' />
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='flex justify-end gap-2 p-4 -m-6 w-auto mt-10 border-t border-gray-200 dark:border-gray-700'>
                        <Button onClick={handleOnRequestClose}><Trans i18nKey={`facebookIntegration.cancel`}>Cancel</Trans></Button>
                        <Button loading={isSubmitting} disabled={!pageID} variant="solid" onClick={handleSubmit} icon={<img src="/img/socials/facebook.svg" className="!h-4 !w-4 shrink-0" />}>&nbsp; <Trans i18nKey={`facebookIntegration.connectFacebookPage`}>Connect Facebook Page</Trans></Button>
                    </div>
                </>
            }
        </Dialog>
    )
}

export default FacebookPageSelectModal
